/**
 * Dashboard page - Smart Queue Dashboard
 *
 * Redesigned to focus on actionable items:
 * 1. Portfolio Snapshot - compact summary row
 * 2. Attention Queue - prioritized action items
 * 3. Charts - collapsible, expanded on demand
 */
import { useState } from 'react'
import { useAttentionQueue } from '../hooks'
import { usePortfolioSummary, usePortfolioHistory, useAllocation, useHoldingsPerformance } from '../hooks/useAnalytics'
import { usePortfolioFilter } from '../contexts'
import { Card, CardHeader, CardBody, EmptyState, LoadingCard } from '../components/common'
import { GettingStartedWidget } from '../components/onboarding/GettingStartedWidget'
import { PortfolioSnapshot, AttentionQueue } from '../components/dashboard'
import { PortfolioValueChart, AllocationPieChart, PerformanceBarChart } from '../components/charts'
import type { AllocationGroupBy, PerformanceSortBy } from '../types/analytics'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
}

type TimeRange = 30 | 90 | 365

export function DashboardPage() {
  // Global portfolio filter
  const { selectedPortfolioId, selectedPortfolio } = usePortfolioFilter()

  // Chart controls
  const [chartsExpanded, setChartsExpanded] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>(30)
  const [allocationGroupBy, setAllocationGroupBy] = useState<AllocationGroupBy>('account')
  const [performanceSortBy, setPerformanceSortBy] = useState<PerformanceSortBy>('gain_pct')

  // Data fetching - filtered by selected portfolio
  const { data: portfolio, isLoading, error } = usePortfolioSummary({
    portfolioId: selectedPortfolioId || undefined,
  })
  const attentionQueue = useAttentionQueue()

  // Chart data - filtered by selected portfolio
  const { data: historyData, isLoading: historyLoading } = usePortfolioHistory({
    days: timeRange,
    portfolioId: selectedPortfolioId || undefined,
  })
  const { data: allocationData, isLoading: allocationLoading } = useAllocation({
    groupBy: allocationGroupBy,
    portfolioId: selectedPortfolioId || undefined,
  })
  const { data: performanceData, isLoading: performanceLoading } = useHoldingsPerformance({
    sortBy: performanceSortBy,
    limit: 10,
    portfolioId: selectedPortfolioId || undefined,
  })


  if (isLoading) {
    return <LoadingCard />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load portfolio data</p>
      </div>
    )
  }

  if (!portfolio) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Portfolio Dashboard</h1>
        <GettingStartedWidget />
        <EmptyState
          icon="chart"
          title="No Portfolio Data"
          description="Add holdings to see your portfolio summary, charts, and performance metrics."
          action={{
            label: 'Add Holdings',
            onClick: () => window.location.href = '/holdings',
          }}
        />
      </div>
    )
  }

  // Page title based on selected portfolio
  const pageTitle = selectedPortfolio
    ? `${selectedPortfolio.name} Dashboard`
    : 'Portfolio Dashboard'

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
        {selectedPortfolio && (
          <span className="px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-full">
            Filtered
          </span>
        )}
      </div>

      {/* Getting Started widget for new users */}
      <GettingStartedWidget />

      {/* Portfolio Snapshot - compact summary row */}
      <PortfolioSnapshot
        totalValue={portfolio.total_value}
        totalCost={portfolio.total_cost_basis}
        gainLoss={portfolio.total_gain_loss}
        gainLossPct={portfolio.gain_loss_pct}
        alertCount={attentionQueue.counts.total}
        lastUpdated={portfolio.as_of}
        isLoading={isLoading}
      />

      {/* Attention Queue - main focus */}
      <AttentionQueue
        items={attentionQueue.items}
        counts={attentionQueue.counts}
        isLoading={attentionQueue.isLoading}
        error={attentionQueue.error}
      />

      {/* Collapsible Charts Section */}
      <div className="border border-gray-200 rounded-lg bg-white">
        <button
          onClick={() => setChartsExpanded(!chartsExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <span className="font-medium text-gray-900">Charts & Analytics</span>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${chartsExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {chartsExpanded && (
          <div className="border-t border-gray-200 p-4 space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Value</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatCurrency(portfolio.total_value)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Cost Basis</h3>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatCurrency(portfolio.total_cost_basis)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Gain/Loss</h3>
                <p className={`mt-1 text-2xl font-semibold ${(portfolio.total_gain_loss ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(portfolio.total_gain_loss)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500">Return</h3>
                <p className={`mt-1 text-2xl font-semibold ${(portfolio.gain_loss_pct ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatPercent(portfolio.gain_loss_pct)}
                </p>
              </div>
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Portfolio value chart */}
              <Card padding="none">
                <CardHeader
                  title="Portfolio Value"
                  action={
                    <div className="flex gap-1">
                      {([30, 90, 365] as TimeRange[]).map((days) => (
                        <button
                          key={days}
                          onClick={() => setTimeRange(days)}
                          className={`px-3 py-1 text-xs rounded-full transition-colors ${
                            timeRange === days
                              ? 'bg-blue-100 text-blue-700 font-medium'
                              : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          {days === 365 ? '1Y' : `${days}D`}
                        </button>
                      ))}
                    </div>
                  }
                />
                <CardBody>
                  {historyLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <p className="text-gray-400">Loading chart...</p>
                    </div>
                  ) : historyData?.data ? (
                    <PortfolioValueChart data={historyData.data} height={280} />
                  ) : (
                    <div className="h-64 flex items-center justify-center">
                      <p className="text-gray-400">No history data</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Allocation pie chart */}
              <Card padding="none">
                <CardHeader
                  title="Allocation"
                  action={
                    <select
                      value={allocationGroupBy}
                      onChange={(e) => setAllocationGroupBy(e.target.value as AllocationGroupBy)}
                      className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="account">By Account</option>
                      <option value="portfolio">By Portfolio</option>
                      <option value="tag">By Tag</option>
                    </select>
                  }
                />
                <CardBody>
                  {allocationLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <p className="text-gray-400">Loading chart...</p>
                    </div>
                  ) : allocationData?.data ? (
                    <AllocationPieChart data={allocationData.data} height={280} />
                  ) : (
                    <div className="h-64 flex items-center justify-center">
                      <p className="text-gray-400">No allocation data</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Performance chart */}
            <Card padding="none">
              <CardHeader
                title="Holdings Performance"
                action={
                  <select
                    value={performanceSortBy}
                    onChange={(e) => setPerformanceSortBy(e.target.value as PerformanceSortBy)}
                    className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="gain_pct">Top Gainers (%)</option>
                    <option value="loss_pct">Top Losers (%)</option>
                    <option value="gain_abs">Top Gainers ($)</option>
                    <option value="loss_abs">Top Losers ($)</option>
                  </select>
                }
              />
              <CardBody>
                {performanceLoading ? (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-400">Loading chart...</p>
                  </div>
                ) : performanceData?.data ? (
                  <PerformanceBarChart
                    data={performanceData.data}
                    height={280}
                    showAbsolute={performanceSortBy.includes('abs')}
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-400">No performance data</p>
                  </div>
                )}
              </CardBody>
            </Card>

          </div>
        )}
      </div>

      {/* Stats footer */}
      <div className="text-sm text-gray-500 text-right">
        {portfolio.accounts_count} accounts | {portfolio.holdings_count} holdings |
        Updated {new Date(portfolio.as_of).toLocaleString()}
      </div>
    </div>
  )
}
